import { useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_DATA_STALE_TIME_MS } from 'app/modules/analytics/config/analyticsConfig';
import NewAnalyticsApi from 'api/new-analytics';
import { useAnalyticsQueryDescriptor } from 'app/modules/analytics/hooks/useAnalyticsQueryDescriptor';
import {
  analyticsDescriptorKey,
  hasAnalyticsQueryablePages,
} from 'app/modules/analytics/utils/analyticsQueryDescriptor';
import {
  isAudienceResponseIncomplete,
  markAnalyticsQueryStaleIfEmpty,
} from 'app/modules/analytics/utils/analyticsIncompleteResponseRefetch';
import { ApiClientError } from 'api/client';
import { useToast } from 'app/hooks/useToast';
import type { AudienceResponse } from 'api/new-analytics/types';

/**
 * Follower demographics are a snapshot of the audience as it stands now, not a window
 * aggregate, so this query is deliberately keyed on the pages alone — changing the
 * reporting period must not empty the cards or trigger a refetch.
 */
export function useAudienceQuery() {
  const descriptor = useAnalyticsQueryDescriptor();
  const { pageIds } = descriptor;
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const audienceQueryKey = analyticsDescriptorKey.audience(descriptor);

  return useQuery<AudienceResponse, ApiClientError>(
    audienceQueryKey,
    () => NewAnalyticsApi.getAudience({ page_ids: pageIds }),
    {
      keepPreviousData: true,
      staleTime: ANALYTICS_DATA_STALE_TIME_MS,
      enabled: hasAnalyticsQueryablePages(descriptor),
      onSuccess: (data) => {
        markAnalyticsQueryStaleIfEmpty(
          queryClient,
          audienceQueryKey,
          isAudienceResponseIncomplete(data),
        );
      },
      onError: (e) => {
        toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
      },
    },
  );
}
